import {
  createBillingProfile,
  updateBillingProfile,
} from '@/lib/recepthor-api';
import { Button, Input, Modal } from '@/ui';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import {
  EMAIL_RE,
  formatRutInput,
  isCompleteLocal,
  localFromStored,
  validateRutInput,
} from '@/lib';
import PhoneInput from '@/shared/OrgModales/PhoneInput.jsx';
import ActecoSelect from './ActecoSelect.jsx';

const LABEL_STYLE = {
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: 'var(--ink-2)',
};

const ERROR_STYLE = {
  fontSize: '0.75rem',
  color: 'var(--err)',
};

function Field({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  required,
  mono,
  note,
  disabled,
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label htmlFor={id} style={LABEL_STYLE}>
        {label}
        {required ? ' *' : ''}
      </label>
      <Input
        id={id}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        error={!!error}
        variant={mono ? 'mono' : 'text'}
        disabled={disabled}
        readOnly={disabled}
      />
      {error && <span style={ERROR_STYLE}>{error}</span>}
      {!error && note && (
        <span
          style={{ fontSize: '0.75rem', color: 'var(--ink-2)', opacity: 0.8 }}
        >
          {note}
        </span>
      )}
    </div>
  );
}

Field.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  error: PropTypes.string,
  required: PropTypes.bool,
  mono: PropTypes.bool,
  note: PropTypes.string,
  disabled: PropTypes.bool,
};

// Required fields per the API create contract. `phone` and `actecoCode` are
// optional; `actecoCode` is derived from the selected activity (ActecoSelect).
const REQUIRED_FIELDS = {
  rut: 'El RUT es requerido.',
  legalName: 'La razón social es requerida.',
  businessActivity: 'La actividad es requerida.',
  address: 'La dirección es requerida.',
  city: 'La ciudad es requerida.',
  commune: 'La comuna es requerida.',
  email: 'El email es requerido.',
};

// Pure so the submit path and the live per-field reading share it.
function validateForm(form) {
  const errors = {};
  for (const [key, message] of Object.entries(REQUIRED_FIELDS)) {
    if (!form[key].trim()) errors[key] = message;
  }
  // Empty RUT already carries the required-field message; the helper only
  // checks format + check digit on a present value.
  if (!errors.rut) {
    const rutError = validateRutInput(form.rut.trim());
    if (rutError) errors.rut = rutError;
  }
  if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) {
    errors.email = 'Email inválido.';
  }
  const phoneLocal = localFromStored(form.phone);
  if (phoneLocal.length > 0 && !isCompleteLocal(phoneLocal)) {
    errors.phone = 'Teléfono incompleto. Formato: +56 9 1234 1234.';
  }
  return errors;
}

export default function EditarFacturacionModal({ profile, onClose, onSaved }) {
  const isEdit = Boolean(profile?.billingProfileId);

  const [form, setForm] = useState({
    rut: profile?.rut ?? '',
    legalName: profile?.legalName ?? '',
    businessActivity: profile?.businessActivity ?? '',
    address: profile?.address ?? '',
    city: profile?.city ?? '',
    commune: profile?.commune ?? '',
    email: profile?.email ?? '',
    phone: profile?.phone ?? '',
    actecoCode: profile?.actecoCode ?? '',
  });
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Field errors are derived, never stored: a field validates from its first
  // keystroke, so correcting it clears the message with no submit involved.
  // Untouched fields stay clean until a blocked submit reveals every one.
  const allErrors = validateForm(form);
  const fieldErrors = submitted
    ? allErrors
    : Object.fromEntries(
        Object.entries(allErrors).filter(([key]) => touched[key]),
      );

  const markTouched = (key) =>
    setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));

  const setField = (key) => (e) => {
    const { value } = e.target;
    markTouched(key);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // ACTECO catalog (674 SII codes, ~167KB) is lazy-loaded as its own chunk so
  // it never lands in the main bundle — it is only fetched when this modal opens.
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [activity, setActivity] = useState(null);

  useEffect(() => {
    let active = true;
    import('@/assets/acteco.json')
      .then((mod) => {
        if (!active) return;
        setCatalog(mod.default ?? mod);
        setCatalogLoading(false);
      })
      .catch(() => {
        if (active) setCatalogLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // Prefill the selected activity from an existing profile once the catalog
  // is available (match by ACTECO code).
  useEffect(() => {
    if (!catalog.length || !profile?.actecoCode) return;
    const match = catalog.find((o) => o.code === profile.actecoCode);
    if (match) setActivity(match);
  }, [catalog, profile]);

  // Selecting an activity drives BOTH fields: businessActivity (description)
  // and actecoCode (derived, read-only). Single source of truth → they cannot
  // drift apart.
  function handleActivityChange(option) {
    setActivity(option);
    markTouched('businessActivity');
    setForm((prev) => ({
      ...prev,
      businessActivity: option?.activity ?? '',
      actecoCode: option?.code ?? '',
    }));
  }

  async function handleSubmit(e) {
    e?.preventDefault();
    setError('');
    setSubmitted(true);

    // `submitted` above already reveals every error through fieldErrors.
    if (Object.keys(allErrors).length > 0) return;

    const body = {
      // Normalized (dots stripped, dash ensured) even if the field never blurred.
      rut: formatRutInput(form.rut.trim()),
      legalName: form.legalName.trim(),
      businessActivity: form.businessActivity.trim(),
      address: form.address.trim(),
      city: form.city.trim(),
      commune: form.commune.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || null,
      actecoCode: form.actecoCode.trim() || null,
    };

    setLoading(true);
    try {
      if (isEdit) {
        await updateBillingProfile(profile.billingProfileId, body);
      } else {
        await createBillingProfile(body);
      }
      await onSaved?.();
    } catch {
      setError('No se pudo guardar. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal open onClose={onClose} size="large">
      <Modal.Header
        title={
          isEdit
            ? 'Editar datos de facturación'
            : 'Agregar datos de facturación'
        }
        onClose={onClose}
      />
      <Modal.Body>
        <form
          id="ef-form"
          onSubmit={handleSubmit}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
          }}
        >
          <Field
            id="ef-legal-name"
            label="Razón social"
            value={form.legalName}
            onChange={setField('legalName')}
            error={fieldErrors.legalName}
            required
            mono
          />
          <Field
            id="ef-rut"
            label="RUT"
            value={form.rut}
            onChange={setField('rut')}
            onBlur={() =>
              setForm((prev) => ({ ...prev, rut: formatRutInput(prev.rut) }))
            }
            error={fieldErrors.rut}
            required
            mono
          />
          <Field
            id="ef-email"
            label="Email"
            value={form.email}
            onChange={setField('email')}
            error={fieldErrors.email}
            required
            mono
          />
          <PhoneInput
            id="ef-phone"
            label="Teléfono"
            value={form.phone}
            onChange={(v) => {
              markTouched('phone');
              setForm((prev) => ({ ...prev, phone: v }));
            }}
            error={fieldErrors.phone}
          />
          <div style={{ gridColumn: '1 / -1' }}>
            <ActecoSelect
              id="ef-activity"
              label="Actividad"
              required
              options={catalog}
              loading={catalogLoading}
              value={activity}
              onChange={handleActivityChange}
              error={fieldErrors.businessActivity}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field
              id="ef-address"
              label="Dirección"
              value={form.address}
              onChange={setField('address')}
              error={fieldErrors.address}
              required
              mono
            />
          </div>
          <Field
            id="ef-city"
            label="Ciudad"
            value={form.city}
            onChange={setField('city')}
            error={fieldErrors.city}
            required
            mono
          />
          <Field
            id="ef-commune"
            label="Comuna"
            value={form.commune}
            onChange={setField('commune')}
            error={fieldErrors.commune}
            required
            mono
          />
          {error && (
            <span style={{ ...ERROR_STYLE, gridColumn: '1 / -1' }}>
              {error}
            </span>
          )}
          <button
            type="submit"
            aria-hidden="true"
            style={{ display: 'none' }}
          />
        </form>
      </Modal.Body>
      <Modal.Foot>
        <Button
          variant="ghost"
          type="button"
          onClick={onClose}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          variant="seal"
          type="button"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Guardando…' : 'Guardar'}
        </Button>
      </Modal.Foot>
    </Modal>
  );
}

EditarFacturacionModal.propTypes = {
  profile: PropTypes.shape({
    billingProfileId: PropTypes.string,
    rut: PropTypes.string,
    legalName: PropTypes.string,
    businessActivity: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    commune: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    actecoCode: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};
